import React from 'react';
import { Info } from 'lucide-react';
import { classNames } from '../../utils/classNames';
import { Tooltip } from '../../components/ui/Tooltip';

export const settingsControlClass =
  'cursor-pointer rounded-xl transition duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:opacity-50';

export const SettingsSection = ({ icon: Icon, title, description, action, children, danger = false, className }) => (
  <section
    className={classNames(
      'overflow-hidden rounded-2xl border',
      danger
        ? 'border-rose-200/80 bg-rose-50/50 dark:border-rose-500/25 dark:bg-rose-500/[0.07]'
        : 'border-border/80 bg-white/75 shadow-[0_1px_0_rgba(255,255,255,0.6)] dark:bg-slate-950/30 dark:shadow-none',
      className
    )}
  >
    {title || description ? (
      <header className="flex items-start gap-3 border-b border-border/70 px-4 py-3.5">
        {Icon ? (
          <span
            className={classNames(
              'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl',
              danger
                ? 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-200'
                : 'bg-accent-soft text-accent'
            )}
          >
            <Icon size={16} aria-hidden />
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          {title ? <h3 className="text-sm font-semibold text-text">{title}</h3> : null}
          {description ? <p className="mt-0.5 text-xs leading-5 text-muted">{description}</p> : null}
        </div>
        {action ? <div className="shrink-0 pt-0.5">{action}</div> : null}
      </header>
    ) : null}
    <div className="divide-y divide-border/70">{children}</div>
  </section>
);

export const SettingsRow = ({ title, description, info, children, danger = false, className }) => (
  <div className={classNames('flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4', className)}>
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <p className={classNames('text-sm font-medium', danger ? 'text-rose-700 dark:text-rose-200' : 'text-text')}>
          {title}
        </p>
        {info ? (
          <Tooltip content={info} multiline>
            <span className="inline-flex cursor-help text-muted">
              <Info size={14} aria-hidden />
            </span>
          </Tooltip>
        ) : null}
      </div>
      {description ? <p className="mt-0.5 text-xs leading-5 text-muted">{description}</p> : null}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export const SettingsActionButton = ({ children, danger = false, className, ...props }) => (
  <button
    type="button"
    className={classNames(
      settingsControlClass,
      'inline-flex min-h-10 items-center justify-center gap-1.5 px-3.5 text-sm font-semibold',
      danger
        ? 'border border-rose-200/80 bg-white/80 text-rose-600 hover:bg-rose-50 dark:border-rose-500/30 dark:bg-transparent dark:text-rose-300 dark:hover:bg-rose-500/10'
        : 'border border-border bg-white/85 text-text hover:border-accent/40 hover:bg-accent-soft/50 dark:bg-slate-950/40',
      className
    )}
    {...props}
  >
    {children}
  </button>
);

export const ThemeSwatch = ({ mode }) => {
  if (mode === 'dark') {
    return (
      <div className="h-[4.25rem] overflow-hidden rounded-xl bg-[#0b1526] p-2.5 shadow-inner">
        <div className="flex gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-400/80" />
          <span className="h-1.5 w-1.5 rounded-full bg-amber-300/80" />
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
        </div>
        <div className="mt-2.5 space-y-1.5">
          <div className="h-1.5 w-10 rounded-full bg-[#68d5ff]/50" />
          <div className="h-1.5 w-16 rounded-full bg-white/15" />
          <div className="h-1.5 w-12 rounded-full bg-white/10" />
        </div>
      </div>
    );
  }

  if (mode === 'system') {
    return (
      <div className="flex h-[4.25rem] overflow-hidden rounded-xl shadow-inner">
        <div className="w-1/2 bg-[#f6f1e8] p-2">
          <div className="h-1.5 w-8 rounded-full bg-[#0f3d91]/35" />
          <div className="mt-2 h-1.5 w-10 rounded-full bg-[#162033]/15" />
        </div>
        <div className="w-1/2 bg-[#0b1526] p-2">
          <div className="h-1.5 w-8 rounded-full bg-[#68d5ff]/45" />
          <div className="mt-2 h-1.5 w-10 rounded-full bg-white/20" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-[4.25rem] overflow-hidden rounded-xl bg-[#f6f1e8] p-2.5 shadow-inner">
      <div className="flex gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      </div>
      <div className="mt-2.5 space-y-1.5">
        <div className="h-1.5 w-10 rounded-full bg-[#0f3d91]/35" />
        <div className="h-1.5 w-16 rounded-full bg-[#162033]/12" />
        <div className="h-1.5 w-12 rounded-full bg-[#162033]/8" />
      </div>
    </div>
  );
};
